import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import MobileNav from './MobileNav';
import MobileSearch from './MobileSearch';
import Button from 'components/UI/Button';
import { useTrans, useLang } from 'utils/localisation';
import { lockUnlockPage } from 'utils';
import { pushSearchEvent } from 'utils/analytics';
import { pushHeaderEvent } from '../../utils';

import * as styles from './MobileMenu.module.scss';

const initialState = [{ previous: null, current: null }];

export default function MobileMenu({
  links,
  open,
  enableSwitcher,
  isIndexPage,
  contactUsLink,
  setActiveMobileMenu,
}) {
  const lang = useLang();
  const t = useTrans(['header', 'common']);
  const preparedLinks = composeMobileLinks(links);
  const [state, setState] = useState(initialState);
  const searchComponentVisible = lang === 'en';
  const [searchActive, setSearchActive] = useState(false);
  const nonInitialState = state.length > 1;
  const showBackButton = nonInitialState || searchActive;

  const setNextActivePanel = payload => {
    setState(prev => [...prev, payload]);
    pushHeaderEvent('click_on_mobile_menu_item', payload.current, 'Mobile');
  };

  const handleBack = () => {
    if (searchActive) {
      setSearchActive(false);
      pushSearchEvent({
        action: 'click_on_back_button',
        deviceType: 'Mobile',
        pageUrl: window.location.href,
      });
      return;
    }
    if (nonInitialState) {
      setState(prev => prev.slice(0, prev.length - 1));
      pushHeaderEvent('click_on_back_button', null, 'Mobile');
    }
  };

  useEffect(() => {
    if (open) {
      lockUnlockPage.lock();
    } else {
      lockUnlockPage.unlock();
    }
    return () => {
      lockUnlockPage.unlock();
    };
  }, [open]);

  const onTransitionEnd = e => {
    if (!open && e.target.className.includes('mobileMenu') && e.propertyName === 'height') {
      setState(initialState);
      setSearchActive(false);
    }
  };

  return (
    <div className={cn(styles.mobileMenu, open && styles.open)} onTransitionEnd={onTransitionEnd}>
      <div
        className={cn(styles.backButton, showBackButton && styles.visible)}
        role="button"
        onClick={handleBack}
      >
        {t('back')}
      </div>
      {searchComponentVisible && (
        <MobileSearch
          className={styles.search}
          searchActive={searchActive}
          setSearchActive={setSearchActive}
          setActiveMobileMenu={setActiveMobileMenu}
          mobileMenuOpen={open}
        />
      )}
      <div className={cn(styles.container, searchActive && styles.hidden)}>
        <MobileNav
          links={preparedLinks}
          enableSwitcher={enableSwitcher}
          isIndexPage={isIndexPage}
          state={state}
          setNextActivePanel={setNextActivePanel}
          open={open}
          searchActive={searchActive}
        />
      </div>
      <div className={styles.buttonLine}>
        <Button
          tag="a"
          href={contactUsLink}
          fullWidthMobile
          onClick={() => pushHeaderEvent('click_on_button', '/contact-us', 'Mobile')}
        >
          {t('contactUsButton')}
        </Button>
      </div>
    </div>
  );
}

function composeMobileLinks(links) {
  const result = [];
  const panelCache = new Map();

  const createLinkItem = item => {
    const linkItem = { title: item.title };

    if (item.url) {
      linkItem.url = item.url;
    } else if (item.to && !item.submenu) {
      linkItem.url = item.to;
    } else if (item.parentLink && !item.to && hasImages(item)) {
      linkItem.url = item.parentLink.url;
    }

    return linkItem;
  };

  const hasImages = item => {
    return Boolean(item.contentType) || item.linkList?.some(({ img }) => Boolean(img));
  };

  const addItemToPanel = (panel, item, parentTitle) => {
    const existingPanel = panelCache.get(parentTitle);
    const targetPanel = existingPanel || panel;

    const isDuplicate = targetPanel.items.some(({ title }) => title === item.title);
    if (!isDuplicate) {
      targetPanel.items.push(item);
    }
  };

  const addParentLinkToPanel = (panel, parentLink) => {
    if (parentLink) {
      panel.items.push(parentLink);
    }
  };

  const processSubmenu = item => {
    if (item.submenu) {
      preparePanel(item.submenu, item.title, item.parentLink);
    }
    if (item.linkList) {
      preparePanel(item.linkList, item.title, item.parentLink);
    }
  };

  const preparePanel = (itemArray, parentTitle, parentLink) => {
    if (!Array.isArray(itemArray)) return;

    const panel = {
      parent: parentTitle || null,
      items: [],
    };

    itemArray.forEach(item => {
      const linkItem = createLinkItem(item);
      processSubmenu(item);
      addItemToPanel(panel, linkItem, parentTitle);
    });

    addParentLinkToPanel(panel, parentLink);

    if (!panelCache.has(parentTitle)) {
      panelCache.set(parentTitle, panel);
      result.push(panel);
    }
  };

  preparePanel(links);
  return result;
}
